import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Company } from '../../../models/company.model';
import { CompanyService } from '../../../services/company.service';
import { ComponentCommService } from '../../../services/component-comm.service';
import { DataDogLoggerService } from '../../../services/data-dog-logger.service';
import { CompanyGroup } from '../../models/company-group.model';
import { CompanyGroupsService } from '../../services/company-groups.service';

@Component({
  selector: 'app-company-groups',
  templateUrl: './company-groups.component.html'
})
export class CompanyGroupsComponent implements OnInit, OnDestroy {

  @ViewChild('groupModal', { static: true }) private readonly groupModal: TemplateRef<any>;

  public company: Company;
  public groups: CompanyGroup[];
  /** Group being created or edited in the modal. */
  public group: CompanyGroup;
  public isUnique: boolean = true;
  /**
   * Flag used to enable/disable UI buttons and links when an API request is in
   * progress.
   */
  public processing: boolean;

  private modalRef: BsModalRef;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private modalService: BsModalService,
    private componentComm: ComponentCommService,
    private companyGroupsService: CompanyGroupsService,
    private dataDogLoggerService: DataDogLoggerService,
    /** @ignore */
    public companyService: CompanyService
  ) { }

  /** @ignore */
  ngOnInit(): void {
    this.componentComm.emit({ name: 'app-title', title: 'COMPANY_GROUPS.TITLE' });

    this.companyService.watch()
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        if (!company) return;

        this.company = company;
        if (!this.company.market.configuration.order?.scopes?.private?.enabled) {
          this.router.navigateByUrl('/company/' + this.company.id + '/setup');
          return;
        }

        this.companyGroupsService.watch(this.company.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(groups => {
            this.groups = groups;
          });
      });
  }

  public create(): void {
    this.isUnique = true;
    this.group = new CompanyGroup({ members: [] });
    this.openModal();
  }

  public edit(group: CompanyGroup): void {
    this.processing = true;

    this.companyGroupsService.getOne(this.company.id, group.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: fullGroup => {
          this.processing = false;
          this.isUnique = true;
          this.group = fullGroup;
          this.openModal();
        },
        error: error => {
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  public submit(): void {
    if (!this.isUniqueName(this.group)) return;

    this.processing = true;
    const request = this.group.id ?
      this.companyGroupsService.update(this.company.id, this.group) :
      this.companyGroupsService.create(this.company.id, this.group);

    request
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.processing = false;
          this.closeModal();
        },
        error: error => {
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  public delete(group: CompanyGroup): void {
    this.processing = true;

    this.companyGroupsService.delete(this.company.id, group)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.processing = false;
          this.groups = this.groups.filter(item => item.id !== group.id);
        },
        error: error => {
          this.processing = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  private isUniqueName(group: CompanyGroup): boolean {
    const name = (group.name || '').trim().toLowerCase();
    this.isUnique = !(this.groups || []).some(item => item.id !== group.id && item.name.toLowerCase() === name);
    return this.isUnique;
  }

  private openModal(): void {
    this.modalRef = this.modalService.show(this.groupModal, { class: 'modal-lg' });
    this.modalRef.onHide
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.modalRef = undefined;
        this.processing = false;
      });
  }

  /** Closes the most recent opened modal. */
  public closeModal(): void {
    if (this.modalRef) this.modalRef.hide();
  }

  /** @ignore */
  ngOnDestroy(): void {
    this.closeModal();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
